import {
  DraggableWindow,
  preventInteractions,
  useClickOutside,
} from "@hoylu/client-common";
import Scrollbar from "@hoylu/client-common/dist/assets/css.modules/scrollbar.module.css";
import React, { useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { cancelDashboardOption } from "../../state/workspaces/workspaces.actions";
import {
  getDialogPosition,
  selectTaskLocationData,
  selectWorkspaces,
} from "../../state/workspaces/workspaces.selector";
import { useI18n } from "../../utils/hooks/use.i18n";
import { generateDeepLink } from "../../utils/url.helpers";
import Styles from "./task.locations.modal.module.css";

export const TaskLocationsModal = () => {
  const dispatch = useDispatch();

  const t = useI18n("ANALYTICS.");

  const cancel = () => {
    dispatch(cancelDashboardOption());
  };

  const modalRef = useClickOutside(cancel);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const data = useSelector(selectTaskLocationData);
  const position = useSelector(getDialogPosition);
  const workspaces = useSelector(selectWorkspaces);

  /** [workspaceId, workspaceName][] */
  const locations: [string, string][] = useMemo(() => {
    if (!data) return [];

    return data.workspaces.map((workspaceId) => [
      workspaceId,
      workspaces.find((w) => w.workspaceId === workspaceId)?.workspaceName ??
        workspaceId,
    ]);
  }, [data, workspaces]);

  useEffect(() => {
    const handleScroll = (event: Event) => {
      if (!scrollRef.current) return;

      if (!scrollRef.current.contains(event.target as Node)) {
        cancel();
      }
    };

    document.addEventListener("scroll", handleScroll, true);

    return () => {
      document.removeEventListener("scroll", handleScroll);
    };
  }, [cancel]);

  if (!data) {
    return null;
  }

  const handleLocationClick = (workspaceId: string) => {
    window.open(
      generateDeepLink(
        "element",
        workspaceId,
        data.taskId.replaceAll("-", ""),
      )!,
      workspaceId,
    );
  };

  return (
    <DraggableWindow
      dragWholeWindow={true}
      fitContent={true}
      initialPosition={position}
      style={{
        zIndex: "var(--modal-position)",
        position: "absolute",
      }}
      testId="task-locations-modal"
    >
      <div ref={modalRef}>
        <div className={Styles.header}>
          <span className={[Styles.icon, "hoylu-ui-icons-blank"].join(" ")} />
          <span>{t("LOCATIONS_LIST")}</span>
        </div>
        <div
          className={[Styles.locationsList, Scrollbar.default].join(" ")}
          ref={scrollRef}
        >
          {locations.map(([workspaceId, workspaceName]) => (
            <div className={Styles.locationRow} key={workspaceId}>
              <span className={Styles.locationName}>{workspaceName}</span>
              <span
                {...preventInteractions()}
                className={[
                  Styles.icon,
                  Styles.clickable,
                  "hoylu-ui-icons-link-from-task",
                ].join(" ")}
                onClick={() => handleLocationClick(workspaceId)}
              />
            </div>
          ))}
        </div>
      </div>
    </DraggableWindow>
  );
};
